import { Injectable } from '@angular/core';

export interface ImageUploadResult {
  base64: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  preview: string;
}

@Injectable({
  providedIn: 'root'
})
export class ImageService {

  constructor() { }

  /**
   * Dosyayı base64'e çevirir
   */
  convertToBase64(file: File): Promise<ImageUploadResult> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1]; // "data:image/jpeg;base64," kısmını çıkar
        
        resolve({
          base64: base64,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          preview: result // Önizleme için tam data URL
        });
      };
      
      reader.onerror = () => {
        reject(new Error('Dosya okuma hatası'));
      };
      
      reader.readAsDataURL(file);
    });
  }

  /**
   * Dosya tipini kontrol eder
   */
  validateImageFile(file: File): { valid: boolean; error?: string } {
    // Dosya boyutu kontrolü (5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return { valid: false, error: 'Dosya boyutu 5MB\'dan büyük olamaz.' };
    }

    // Dosya tipi kontrolü
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'Sadece JPEG, PNG, GIF ve WebP formatları desteklenir.' };
    }

    return { valid: true };
  }

  /**
   * Görsel boyutlandırır (opsiyonel)
   */
  resizeImage(file: File, maxWidth: number = 800, maxHeight: number = 600, quality: number = 0.8): Promise<ImageUploadResult> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Orijinal boyutları al
        let { width, height } = img;

        // Boyut oranını koru
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }

        // Canvas'ı boyutlandır
        canvas.width = width;
        canvas.height = height;

        // Resmi çiz
        ctx?.drawImage(img, 0, 0, width, height);

        // Base64'e çevir
        canvas.toBlob((blob) => {
          if (blob) {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result as string;
              const base64 = result.split(',')[1];
              
              resolve({
                base64: base64,
                fileName: file.name,
                fileSize: blob.size,
                fileType: blob.type,
                preview: result
              });
            };
            reader.readAsDataURL(blob);
          } else {
            reject(new Error('Görsel boyutlandırma hatası'));
          }
        }, file.type, quality);
      };

      img.onerror = () => {
        reject(new Error('Görsel yükleme hatası'));
      };

      // Dosyayı img elementine yükle
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * Base64'ten önizleme URL'i oluşturur
   */
  getPreviewUrl(base64: string, mimeType: string = 'image/jpeg'): string {
    return `data:${mimeType};base64,${base64}`;
  }

  /**
   * Dosya boyutunu okunabilir formata çevirir
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Default avatar oluşturur
   */
  generateDefaultAvatar(initials: string, size: number = 100): string {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    canvas.width = size;
    canvas.height = size;
    
    if (ctx) {
      // Arka plan rengi
      const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'];
      const color = colors[initials.charCodeAt(0) % colors.length];
      
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, size, size);
      
      // Yazı
      ctx.fillStyle = '#FFFFFF';
      ctx.font = `bold ${size / 2.5}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(initials, size / 2, size / 2);
    }
    
    return canvas.toDataURL('image/png');
  }
}
